import { useCallback, useEffect, useRef } from 'react'

type UseFeedbackModalSchedulerOptions = {
  blocked: boolean
  cooldownMs: number
  excludedPaths: readonly string[]
  excludedPathPrefixes?: readonly string[]
  initialDelayMs: number
  onTrigger: () => void
  paused: boolean
  path: string
  pausedPathPrefixes: readonly string[]
  resetOnExternalResume?: boolean
  routeDelayMs: number
  shouldPrompt: boolean
}

function pathMatchesPrefix (path: string, prefix: string) {
  return path === prefix || path.startsWith(`${prefix}/`)
}

function isSchedulerPaused ({
  path,
  paused,
  pausedPathPrefixes
}: Pick<UseFeedbackModalSchedulerOptions, 'path' | 'paused' | 'pausedPathPrefixes'>) {
  return paused || pausedPathPrefixes.some(prefix => pathMatchesPrefix(path, prefix))
}

function isSchedulerExcluded ({
  excludedPaths,
  excludedPathPrefixes,
  path
}: Pick<UseFeedbackModalSchedulerOptions, 'excludedPaths' | 'excludedPathPrefixes' | 'path'>) {
  return (
    excludedPaths.includes(path) ||
    excludedPathPrefixes?.some(prefix => pathMatchesPrefix(path, prefix)) === true
  )
}

export function useFeedbackModalScheduler ({
  blocked,
  cooldownMs,
  excludedPaths,
  excludedPathPrefixes = [],
  initialDelayMs,
  onTrigger,
  paused,
  path,
  pausedPathPrefixes,
  resetOnExternalResume = false,
  routeDelayMs,
  shouldPrompt
}: UseFeedbackModalSchedulerOptions) {
  const isInitiallyPaused = isSchedulerPaused({
    path,
    paused,
    pausedPathPrefixes
  })
  const lastRouteChangeAtRef = useRef(Date.now())
  const nextEligibleAtRef = useRef<number | null>(
    shouldPrompt && !isInitiallyPaused ? Date.now() + initialDelayMs : null
  )
  const pausedRemainingMsRef = useRef<number | null>(
    shouldPrompt && isInitiallyPaused ? initialDelayMs : null
  )
  const isTimerPausedRef = useRef(isInitiallyPaused)
  const previousShouldPromptRef = useRef(shouldPrompt)
  const previousExternallyPausedRef = useRef(paused)
  const shouldResetOnUnpauseRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleCheckRef = useRef<() => void>(() => undefined)
  const latestStateRef = useRef({
    blocked,
    cooldownMs,
    excludedPaths,
    excludedPathPrefixes,
    onTrigger,
    paused,
    path,
    pausedPathPrefixes,
    routeDelayMs,
    shouldPrompt
  })

  latestStateRef.current = {
    blocked,
    cooldownMs,
    excludedPaths,
    excludedPathPrefixes,
    onTrigger,
    paused,
    path,
    pausedPathPrefixes,
    routeDelayMs,
    shouldPrompt
  }

  const clearScheduledCheck = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const runCheck = useCallback(() => {
    const state = latestStateRef.current

    if (
      !state.shouldPrompt ||
      state.blocked ||
      isSchedulerExcluded(state) ||
      isSchedulerPaused(state)
    ) {
      return
    }

    state.onTrigger()
    nextEligibleAtRef.current = Date.now() + state.cooldownMs
    scheduleCheckRef.current()
  }, [])

  const scheduleCheck = useCallback(() => {
    const state = latestStateRef.current

    clearScheduledCheck()

    if (
      !state.shouldPrompt ||
      nextEligibleAtRef.current === null ||
      isSchedulerPaused(state)
    ) {
      return
    }

    const { routeDelayMs } = latestStateRef.current
    const nextCheckAt = Math.max(
      nextEligibleAtRef.current,
      lastRouteChangeAtRef.current + routeDelayMs
    )

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
      runCheck()
    }, Math.max(nextCheckAt - Date.now(), 0))
  }, [clearScheduledCheck, runCheck])

  scheduleCheckRef.current = scheduleCheck

  useEffect(() => {
    const wasExternallyPaused = previousExternallyPausedRef.current

    previousExternallyPausedRef.current = paused

    if (!resetOnExternalResume || !paused || wasExternallyPaused) {
      return
    }

    shouldResetOnUnpauseRef.current = true
  }, [paused, resetOnExternalResume])

  useEffect(() => {
    const isPaused = isSchedulerPaused({
      path,
      paused,
      pausedPathPrefixes
    })

    if (isPaused) {
      if (isTimerPausedRef.current) {
        return
      }

      isTimerPausedRef.current = true
      clearScheduledCheck()

      if (nextEligibleAtRef.current !== null) {
        pausedRemainingMsRef.current = Math.max(nextEligibleAtRef.current - Date.now(), 0)
        nextEligibleAtRef.current = null
      }

      return
    }

    if (!isTimerPausedRef.current) {
      return
    }

    isTimerPausedRef.current = false

    if (shouldResetOnUnpauseRef.current) {
      nextEligibleAtRef.current = Date.now() + initialDelayMs
      pausedRemainingMsRef.current = null
      shouldResetOnUnpauseRef.current = false
    } else if (pausedRemainingMsRef.current !== null) {
      nextEligibleAtRef.current = Date.now() + pausedRemainingMsRef.current
      pausedRemainingMsRef.current = null
    }

    scheduleCheck()
  }, [clearScheduledCheck, initialDelayMs, path, paused, pausedPathPrefixes, scheduleCheck])

  useEffect(() => {
    lastRouteChangeAtRef.current = Date.now()
    scheduleCheck()
  }, [path, routeDelayMs])

  useEffect(() => {
    const wasPromptEnabled = previousShouldPromptRef.current

    previousShouldPromptRef.current = shouldPrompt

    if (!shouldPrompt) {
      nextEligibleAtRef.current = null
      pausedRemainingMsRef.current = null
      shouldResetOnUnpauseRef.current = false
      clearScheduledCheck()

      return
    }

    if (
      !wasPromptEnabled ||
      (
        nextEligibleAtRef.current === null &&
        pausedRemainingMsRef.current === null
      )
    ) {
      if (isSchedulerPaused({ path, paused, pausedPathPrefixes })) {
        pausedRemainingMsRef.current = initialDelayMs
        nextEligibleAtRef.current = null
      } else {
        nextEligibleAtRef.current = Date.now() + initialDelayMs
        pausedRemainingMsRef.current = null
      }
    }

    scheduleCheck()
  }, [
    clearScheduledCheck,
    initialDelayMs,
    path,
    paused,
    pausedPathPrefixes,
    scheduleCheck,
    shouldPrompt
  ])

  useEffect(() => {
    scheduleCheck()
  }, [blocked, scheduleCheck])

  useEffect(() => () => {
    clearScheduledCheck()
  }, [clearScheduledCheck])
}
